<template>
  <nav
    v-if="eventSlug"
    :aria-label="$gettext('Breadcrumb')"
    class=""
  >
    <ol class="flex sm:inline-flex items-stretch justify-stretch text-sm bg-vp-base-150 arrow-shape-right px-0!">
      <li
        v-for="crumb in breadcrumbs"
        :key="crumb.key"
        class="flex items-center flex-1 justify-stretch"
      >
        <component
          :is="crumb.state !== 'next' && crumb.path ? 'a' : 'span'"
          :href="crumb.state !== 'next' ? crumb.path : undefined"
          :class="[
            'h-full',
            'breadcrumb-base',
            getBreadcrumbClasses(crumb),
          ]"
        >
          {{ crumb.name }}
        </component>
      </li>
    </ol>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useCheckoutStore } from './stores/checkout';
import { useGettext } from 'vue3-gettext';

const router = useRouter();
const checkoutStore = useCheckoutStore();
const { $gettext } = useGettext();

const eventSlug = computed(() => checkoutStore.getSelectedEvent?.slug);

export type BreadcrumbKey = 'tickets' | 'checkout' | 'complete';

type Breadcrumb = {
  key: BreadcrumbKey;
  name: string;
  path?: string;
  state: 'previous' | 'current' | 'next';
};

type Props = {
  current: Breadcrumb['key'];
};

const props = defineProps<Props>();

const breadcrumbs = computed<Breadcrumb[]>(() => {
  return [
    {
      key: 'tickets' as const,
      name: $gettext('Tickets'),
      path: router.resolve({ name: 'OrderNew', params: { eventId: eventSlug.value } }).href,
    },
    {
      key: 'checkout' as const,
      name: $gettext('Checkout'),
      path: router.resolve({ name: 'OrderPayment', params: { eventId: eventSlug.value } }).href,
    },
    {
      key: 'complete' as const,
      name: $gettext('Order complete'),
    },
  ].map(b => ({
    ...b,
    state: breadcrumbState(b),
  }));
});

const breadcrumbKeys: Breadcrumb['key'][] = [
  'tickets', 'checkout', 'complete',
];

const currentIndex = computed(() => breadcrumbKeys.indexOf(props.current));

const breadcrumbState = ({ key }: { key: Breadcrumb['key'] }): Breadcrumb['state'] => {
  const keyIndex = breadcrumbKeys.indexOf(key);
  if (keyIndex < currentIndex.value) {
    return 'previous';
  };
  if (keyIndex === currentIndex.value) {
    return 'current';
  };
  return 'next';
};

const getBreadcrumbClasses = (crumb: Breadcrumb) => {
  const classes = [];

  if (crumb.state === 'current') {
    classes.push('breadcrumb-current');
  } else if (crumb.state === 'previous') {
    classes.push('breadcrumb-previous');
  } else {
    classes.push('breadcrumb-next');
  };

  if (crumb.key === 'tickets') {
    classes.push('arrow-shape-right');
  } else {
    classes.push('arrow-shape-both');
  };

  return classes.join(' ');
};
</script>
